import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Container, VStack, HStack, Text, Heading, Button,
  FormControl, FormLabel, Input, Divider, useToast, Alert, AlertIcon,
  Spinner, Badge, Icon, Flex,
} from '@chakra-ui/react';
import { FaFileContract, FaCheck, FaBuilding } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import jsPDF from 'jspdf';
import { supabase } from '../supabaseClient';

export default function FacilityAgreement() {
  const { facilityId } = useParams();
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [form, setForm] = useState({
    signer_name: '',
    signer_title: '',
    signer_email: '',
    signed_date: new Date().toLocaleDateString('en-US'),
  });
  const sigCanvas = useRef(null);
  const toast = useToast();

  useEffect(() => {
    if (facilityId) fetchFacility();
  }, [facilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFacility = async () => {
    try {
      const { data, error } = await supabase
        .from('facilities')
        .select('*')
        .eq('id', facilityId)
        .single();
      if (error) throw error;
      if (data.agreement_signed) {
        setSigned(true);
      }
      setFacility(data);
      setForm(prev => ({
        ...prev,
        signer_name: data.contact_name || '',
        signer_email: data.contact_email || '',
        signer_title: data.contact_title || '',
      }));
    } catch (err) {
      toast({ title: 'Error', description: 'Facility not found or invalid link.', status: 'error', duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const clearSignature = () => {
    sigCanvas.current?.clear();
  };

  const generatePDF = (signatureDataURL) => {
    const doc = new jsPDF();
    const lm = 20;
    const pw = 170;
    let y = 20;

    // Header
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('AtHomeBloodDraw.com', 105, y, { align: 'center' });
    y += 8;
    doc.setFontSize(13);
    doc.text('Facility Services Agreement', 105, y, { align: 'center' });
    y += 10;
    doc.setDrawColor(0, 128, 128);
    doc.setLineWidth(0.5);
    doc.line(lm, y, lm + pw, y);
    y += 10;

    // Facility Info
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Facility Information:', lm, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.text(`Facility: ${facility.name}`, lm, y); y += 5;
    if (facility.address) { doc.text(`Address: ${facility.address}`, lm, y); y += 5; }
    if (facility.city) { doc.text(`City/State: ${facility.city}, ${facility.state || ''} ${facility.zip || ''}`, lm, y); y += 5; }
    doc.text(`Contact: ${form.signer_name}`, lm, y); y += 5;
    doc.text(`Title: ${form.signer_title}`, lm, y); y += 5;
    doc.text(`Email: ${form.signer_email}`, lm, y); y += 10;

    // Agreement Terms
    const sections = [
      {
        title: '1. Services',
        text: 'AtHomeBloodDraw.com ("Provider") agrees to provide mobile phlebotomy services at the Facility. Services include specimen collection, packaging, and coordination of laboratory shipment for patients/residents at the Facility. Provider will supply all necessary equipment and trained, certified phlebotomists.',
      },
      {
        title: '2. Scheduling & Access',
        text: 'Provider and Facility will coordinate scheduling of blood draw visits. Facility agrees to provide reasonable access to patients/residents, appropriate workspace, and timely communication of scheduling needs. Provider will make best efforts to accommodate scheduling requests.',
      },
      {
        title: '3. Compliance & HIPAA',
        text: 'Both parties agree to comply with all applicable federal, state, and local laws, including HIPAA, the Anti-Kickback Statute, and the Stark Law. Provider maintains a Business Associate Agreement (BAA) and will handle all Protected Health Information (PHI) in accordance with HIPAA regulations. All phlebotomists are trained in HIPAA compliance and universal precautions.',
      },
      {
        title: '4. Insurance & Liability',
        text: 'Provider maintains professional liability insurance with minimum coverage of $1,000,000 per occurrence and $3,000,000 aggregate. Provider carries workers compensation insurance for all phlebotomists. Facility is not liable for injuries sustained by Provider personnel during service delivery.',
      },
      {
        title: '5. Billing & Compensation',
        text: 'Billing arrangements will be agreed upon separately and documented in writing. Standard billing may include per-draw fees, monthly service fees, or as otherwise negotiated. Payment terms are Net 30 days from invoice date unless otherwise agreed.',
      },
      {
        title: '6. Term & Termination',
        text: 'This Agreement is effective upon signing and continues until terminated by either party with thirty (30) days written notice. Either party may terminate immediately for cause, including material breach, loss of licensure, or violation of applicable laws.',
      },
      {
        title: '7. Confidentiality',
        text: 'Both parties agree to maintain the confidentiality of proprietary business information, patient information, and the terms of this Agreement. This obligation survives termination of the Agreement.',
      },
    ];

    sections.forEach(section => {
      if (y > 250) { doc.addPage(); y = 20; }
      doc.setFont('helvetica', 'bold');
      doc.text(section.title, lm, y);
      y += 6;
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(section.text, pw);
      doc.text(lines, lm, y);
      y += lines.length * 5 + 6;
    });

    // Signature section
    if (y > 220) { doc.addPage(); y = 20; }
    y += 5;
    doc.setDrawColor(0, 0, 0);
    doc.line(lm, y, lm + pw, y);
    y += 8;
    doc.setFont('helvetica', 'bold');
    doc.text('Authorized Signature:', lm, y);
    y += 8;

    if (signatureDataURL) {
      doc.addImage(signatureDataURL, 'PNG', lm, y, 60, 20);
      y += 22;
    }

    doc.setFont('helvetica', 'normal');
    doc.text(`Name: ${form.signer_name}`, lm, y); y += 5;
    doc.text(`Title: ${form.signer_title}`, lm, y); y += 5;
    doc.text(`Email: ${form.signer_email}`, lm, y); y += 5;
    doc.text(`Date: ${form.signed_date}`, lm, y); y += 5;
    doc.text(`Facility: ${facility.name}`, lm, y);

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('AtHomeBloodDraw.com - Facility Services Agreement', lm, 285);

    return doc;
  };

  const handleSubmit = async () => {
    if (!form.signer_name.trim()) {
      toast({ title: 'Please enter your name', status: 'warning', duration: 3000 });
      return;
    }
    if (!form.signer_email.trim()) {
      toast({ title: 'Please enter your email', status: 'warning', duration: 3000 });
      return;
    }
    if (sigCanvas.current?.isEmpty()) {
      toast({ title: 'Please provide your signature', status: 'warning', duration: 3000 });
      return;
    }

    setSigning(true);
    try {
      const canvas = sigCanvas.current.getCanvas();
      const signatureDataURL = canvas.toDataURL('image/png');

      // Upload signature
      const sigBlob = await (await fetch(signatureDataURL)).blob();
      const sigFileName = `facility-signature-${facilityId}-${Date.now()}.png`;
      const { error: sigUploadErr } = await supabase.storage
        .from('doctor-signatures')
        .upload(sigFileName, sigBlob, { contentType: 'image/png', upsert: true });
      if (sigUploadErr) throw sigUploadErr;

      const { data: sigUrlData } = supabase.storage
        .from('doctor-signatures')
        .getPublicUrl(sigFileName);

      // Generate and upload PDF
      const pdfDoc = generatePDF(signatureDataURL);
      const pdfBlob = pdfDoc.output('blob');
      const pdfFileName = `facility-agreement-${facilityId}-${Date.now()}.pdf`;
      const { error: pdfUploadErr } = await supabase.storage
        .from('doctor-signatures')
        .upload(pdfFileName, pdfBlob, { contentType: 'application/pdf', upsert: true });
      if (pdfUploadErr) throw pdfUploadErr;

      const { data: pdfUrlData } = supabase.storage
        .from('doctor-signatures')
        .getPublicUrl(pdfFileName);

      // Update facility record
      const { error: updateErr } = await supabase
        .from('facilities')
        .update({
          agreement_signed: true,
          agreement_signed_at: new Date().toISOString(),
          agreement_signer_name: form.signer_name.trim(),
          agreement_signer_title: form.signer_title.trim(),
          agreement_signer_email: form.signer_email.trim(),
          agreement_signature_url: sigUrlData.publicUrl,
          agreement_pdf_url: pdfUrlData.publicUrl,
        })
        .eq('id', facilityId);
      if (updateErr) throw updateErr;

      // Download PDF for signer
      pdfDoc.save(`Facility-Agreement-${facility.name.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`);

      // Log to admin activity
      try {
        await supabase.from('admin_activity_log').insert({
          action: 'Facility agreement signed',
          username: 'System',
          details: `${facility.name} — Signed by ${form.signer_name} (${form.signer_title})`,
          created_at: new Date().toISOString(),
        });
      } catch (_) { /* ignore log errors */ }

      setSigned(true);
      toast({ title: 'Agreement signed successfully!', description: 'A PDF copy has been downloaded.', status: 'success', duration: 5000 });
    } catch (err) {
      toast({ title: 'Error signing agreement', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="xl" color="teal.500" />
      </Box>
    );
  }

  if (!facility) {
    return (
      <Box minH="100vh" bg="gray.50" py={16}>
        <Container maxW="600px">
          <Alert status="error" borderRadius="xl"><AlertIcon />Invalid or expired agreement link.</Alert>
        </Container>
      </Box>
    );
  }

  // Already signed
  if (signed) {
    return (
      <Box minH="100vh" bg="gray.50">
        <Box bgGradient="linear(to-r, teal.600, teal.800)" py={10} color="white" textAlign="center">
          <Icon as={FaBuilding} boxSize={10} mb={3} opacity={0.8} />
          <Heading size="lg">AtHomeBloodDraw.com</Heading>
          <Text opacity={0.9}>Facility Services Agreement</Text>
        </Box>
        <Container maxW="600px" py={12}>
          <Box bg="white" borderRadius="2xl" boxShadow="xl" p={10} textAlign="center">
            <Icon as={FaCheck} boxSize={16} color="green.500" mb={4} p={3} bg="green.50" borderRadius="full" />
            <Heading size="lg" color="gray.800" mb={3}>Agreement Signed</Heading>
            <Text color="gray.600" mb={4}>
              Thank you, {facility.name}! Your Facility Services Agreement has been signed and recorded.
              Our team will be in touch to coordinate scheduling and get started.
            </Text>
            <VStack spacing={4}>
              <Badge colorScheme="green" fontSize="md" px={4} py={2} borderRadius="full">Signed & Confirmed</Badge>
              {facility.agreement_pdf_url && (
                <Button
                  colorScheme="teal"
                  variant="outline"
                  size="md"
                  onClick={() => window.open(facility.agreement_pdf_url, '_blank')}
                  leftIcon={<Icon as={FaFileContract} />}
                >
                  View Signed Agreement
                </Button>
              )}
              {facility.agreement_signed_at && (
                <Text fontSize="sm" color="gray.400">
                  Signed on {new Date(facility.agreement_signed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  {facility.agreement_signer_name ? ` by ${facility.agreement_signer_name}` : ''}
                </Text>
              )}
            </VStack>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header */}
      <Box bgGradient="linear(to-r, teal.600, teal.800)" py={10} color="white" textAlign="center">
        <Icon as={FaBuilding} boxSize={10} mb={3} opacity={0.8} />
        <Heading size="lg">AtHomeBloodDraw.com</Heading>
        <Text opacity={0.9} mt={1}>Facility Services Agreement</Text>
      </Box>

      <Container maxW="800px" py={8} px={4}>
        {/* Facility Info */}
        <Box bg="white" borderRadius="xl" boxShadow="md" p={6} mb={6}>
          <HStack spacing={3} mb={4}>
            <Icon as={FaBuilding} color="teal.500" />
            <Heading size="md" color="gray.800">Facility Information</Heading>
          </HStack>
          <VStack align="start" spacing={1} pl={8}>
            <Text fontWeight="700" fontSize="lg" color="teal.700">{facility.name}</Text>
            {facility.address && <Text color="gray.600" fontSize="sm">{facility.address}</Text>}
            {facility.city && <Text color="gray.600" fontSize="sm">{facility.city}, {facility.state} {facility.zip}</Text>}
            {facility.contact_name && <Text color="gray.600" fontSize="sm">Contact: {facility.contact_name}</Text>}
          </VStack>
        </Box>

        {/* Agreement Terms */}
        <Box bg="white" borderRadius="xl" boxShadow="md" p={6} mb={6}>
          <HStack spacing={3} mb={4}>
            <Icon as={FaFileContract} color="teal.500" />
            <Heading size="md" color="gray.800">Agreement Terms</Heading>
          </HStack>

          <VStack align="start" spacing={5} fontSize="sm" color="gray.700">
            <Box>
              <Text fontWeight="700" mb={1}>1. Services</Text>
              <Text>AtHomeBloodDraw.com ("Provider") agrees to provide mobile phlebotomy services at the Facility. Services include specimen collection, packaging, and coordination of laboratory shipment for patients/residents at the Facility. Provider will supply all necessary equipment and trained, certified phlebotomists.</Text>
            </Box>
            <Box>
              <Text fontWeight="700" mb={1}>2. Scheduling & Access</Text>
              <Text>Provider and Facility will coordinate scheduling of blood draw visits. Facility agrees to provide reasonable access to patients/residents, appropriate workspace, and timely communication of scheduling needs.</Text>
            </Box>
            <Box>
              <Text fontWeight="700" mb={1}>3. Compliance & HIPAA</Text>
              <Text>Both parties agree to comply with all applicable federal, state, and local laws, including HIPAA, the Anti-Kickback Statute, and the Stark Law. Provider maintains a Business Associate Agreement (BAA) and will handle all Protected Health Information (PHI) in accordance with HIPAA regulations.</Text>
            </Box>
            <Box>
              <Text fontWeight="700" mb={1}>4. Insurance & Liability</Text>
              <Text>Provider maintains professional liability insurance with minimum coverage of $1,000,000 per occurrence and $3,000,000 aggregate. Provider carries workers compensation insurance for all phlebotomists.</Text>
            </Box>
            <Box>
              <Text fontWeight="700" mb={1}>5. Billing & Compensation</Text>
              <Text>Billing arrangements will be agreed upon separately and documented in writing. Standard billing may include per-draw fees, monthly service fees, or as otherwise negotiated. Payment terms are Net 30 days from invoice date.</Text>
            </Box>
            <Box>
              <Text fontWeight="700" mb={1}>6. Term & Termination</Text>
              <Text>This Agreement is effective upon signing and continues until terminated by either party with thirty (30) days written notice. Either party may terminate immediately for cause.</Text>
            </Box>
            <Box>
              <Text fontWeight="700" mb={1}>7. Confidentiality</Text>
              <Text>Both parties agree to maintain the confidentiality of proprietary business information, patient information, and the terms of this Agreement.</Text>
            </Box>
          </VStack>
        </Box>

        {/* Signing Form */}
        <Box bg="white" borderRadius="xl" boxShadow="md" p={6}>
          <Heading size="md" color="gray.800" mb={4}>Sign Agreement</Heading>
          <VStack spacing={4}>
            <HStack spacing={4} w="full">
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="600">Full Name</FormLabel>
                <Input name="signer_name" value={form.signer_name} onChange={handleChange} placeholder="John Doe" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600">Title</FormLabel>
                <Input name="signer_title" value={form.signer_title} onChange={handleChange} placeholder="Administrator" />
              </FormControl>
            </HStack>
            <HStack spacing={4} w="full">
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="600">Email</FormLabel>
                <Input name="signer_email" type="email" value={form.signer_email} onChange={handleChange} placeholder="john@facility.com" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600">Date</FormLabel>
                <Input name="signed_date" value={form.signed_date} onChange={handleChange} isReadOnly bg="gray.50" />
              </FormControl>
            </HStack>

            <Divider />

            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="600">Signature</FormLabel>
              <Box border="2px" borderColor="gray.200" borderRadius="xl" bg="white" overflow="hidden">
                <SignatureCanvas
                  ref={sigCanvas}
                  canvasProps={{ width: 700, height: 150, className: 'signature-canvas', style: { width: '100%', height: '150px' } }}
                />
              </Box>
              <Flex justify="flex-end" mt={2}>
                <Button size="xs" variant="ghost" colorScheme="red" onClick={clearSignature}>Clear Signature</Button>
              </Flex>
            </FormControl>

            <Alert status="info" borderRadius="md" fontSize="sm">
              <AlertIcon />
              By signing, you agree to the terms above on behalf of {facility.name}. A PDF copy will be downloaded for your records.
            </Alert>

            <Button
              colorScheme="teal"
              size="lg"
              w="full"
              onClick={handleSubmit}
              isLoading={signing}
              loadingText="Signing..."
              fontWeight="700"
              py={7}
              borderRadius="xl"
            >
              Sign Agreement
            </Button>
          </VStack>
        </Box>

        <Text fontSize="xs" color="gray.400" textAlign="center" mt={8}>
          AtHomeBloodDraw.com - Facility Services Agreement
        </Text>
      </Container>
    </Box>
  );
}
